<!-- frontend/src/components/UI/ShipSidebar.vue -->
<template>
  <div class="ship-sidebar h-full flex flex-col" :class="isDarkMode ? 'bg-gray-900' : 'bg-white'">
    <div class="p-2.5 border-b flex-shrink-0" :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'">
      <!-- Toggle Filter Button -->
      <div class="flex items-center justify-between mb-2">
        <button 
          @click="toggleFilterSection"
          class="text-xs transition-colors flex items-center gap-1"
          :class="isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'"
        >
          <i :class="showFilterSection ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-[10px]"></i>
          <span>{{ showFilterSection ? 'Hide Filters' : 'Show Filters' }}</span>
        </button>
        <div class="text-[10px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
          <i class="fas fa-ship mr-1"></i> {{ filteredList.length }} vessels
        </div>
      </div>
      
      <!-- Filter Section - Collapsible -->
      <div v-show="showFilterSection" class="space-y-2">
        <!-- Search -->
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name, MMSI, or Callsign..."
            class="w-full pl-7 pr-7 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'"
          />
          <i class="fas fa-search absolute left-2 top-2 text-gray-400 text-xs"></i>
          <button 
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            :class="isDarkMode ? 'hover:text-gray-300' : 'hover:text-gray-600'"
          >
            <i class="fas fa-times text-xs"></i>
          </button>
        </div>
        
        <!-- Source Filter - ONLY SHOW FOR ADMIN -->
        <div v-if="isAdmin">
          <label class="block text-[10px] font-medium mb-1" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
            <i class="fas fa-filter mr-1 text-blue-500"></i>
            Source Filter
          </label>
          <select
            v-model="sourceFilter"
            class="w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'"
            @change="handleSourceFilterChange"
          >
            <option value="all">All Sources</option>
            <option v-for="source in allSources" :key="source" :value="source">
              {{ source }}
            </option>
          </select>
        </div>
        
        <!-- AIS Status Filter - Menggunakan Select seperti filter lainnya -->
        <div>
          <label class="block text-[10px] font-medium mb-1" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
            <i class="fas fa-signal mr-1 text-green-500"></i>
            AIS Status
          </label>
          <select
            v-model="statusFilter"
            class="w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'"
            @change="handleStatusFilterChange"
          >
            <option value="all">All Ships</option>
            <option value="active">🟢 Active Ships (Last 2 hrs)</option>
            <option value="inactive">⚫ Inactive Ships (&gt;2 hrs)</option>
          </select>
          <p class="text-[9px] mt-1" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
            <i class="fas fa-info-circle mr-1"></i>
            Active: updated in last 2 hours | Inactive: no recent update
          </p>
        </div>
        
        <!-- Type Filter -->
        <div>
          <label class="block text-[10px] font-medium mb-1" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
            <i class="fas fa-ship mr-1 text-purple-500"></i>
            Ship Type
          </label>
          <select
            v-model="typeFilter"
            class="w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'"
            @change="handleTypeFilterChange"
          >
            <option value="all">All Types</option>
            <option value="cargo">Cargo</option>
            <option value="tanker">Tanker</option>
            <option value="passenger">Passenger</option>
            <option value="fishing">Fishing</option>
            <option value="tug">Tug</option>
            <option value="towing">Towing</option>
            <option value="sailing">Sailing</option>
            <option value="pleasure">Pleasure</option>
            <option value="hsc">High Speed Craft</option>
            <option value="wig">Wing In Ground</option>
            <option value="military">Military</option>
            <option value="law">Law Enforcement</option>
            <option value="sar">SAR</option>
            <option value="medical">Medical</option>
            <option value="pilot">Pilot Vessel</option>
            <option value="port_tender">Port Tender</option>
            <option value="anti_pollution">Anti-Pollution</option>
            <option value="dredging">Dredging/Underwater</option>
            <option value="diving">Diving Ops</option>
            <option value="aton">Aid to Navigation</option>
            <option value="other">Other</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </div>
      
      <!-- Active Filters -->
      <div v-if="hasActiveFilters" class="flex justify-between items-center mt-1.5">
        <div class="flex flex-wrap gap-1">
          <span 
            v-if="typeFilter !== 'all'"
            class="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded"
            :class="isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700'"
          >
            Type: {{ getTypeDisplayName(typeFilter) }}
            <button @click="resetTypeFilter" class="ml-1 hover:opacity-70">
              <i class="fas fa-times text-[10px]"></i>
            </button>
          </span>
          <span 
            v-if="searchQuery"
            class="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded"
            :class="isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-700'"
          >
            Search: "{{ searchQuery }}"
            <button @click="searchQuery = ''" class="ml-1 hover:opacity-70">
              <i class="fas fa-times text-[10px]"></i>
            </button>
          </span>
          <span 
            v-if="sourceFilter !== 'all' && isAdmin"
            class="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded"
            :class="isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'"
          >
            Source: {{ sourceFilter }}
            <button @click="resetSourceFilter" class="ml-1 hover:opacity-70">
              <i class="fas fa-times text-[10px]"></i>
            </button>
          </span>
          <span 
            v-if="statusFilter !== 'all'"
            class="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded"
            :class="statusFilter === 'active' 
              ? (isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-700')
              : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')"
          >
            <i class="fas fa-circle text-[8px] mr-1" :class="statusFilter === 'active' ? 'text-green-500' : 'text-gray-400'"></i>
            {{ statusFilter === 'active' ? 'Active Only' : 'Inactive Only' }}
            <button @click="resetStatusFilter" class="ml-1 hover:opacity-70">
              <i class="fas fa-times text-[10px]"></i>
            </button>
          </span>
        </div>
        <button
          v-if="hasActiveFilters"
          @click="clearAllFilters"
          class="text-[10px] underline transition-colors"
          :class="isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'"
        >
          Clear all
        </button>
      </div>
    </div>
    
    <!-- Scrollable Content dengan Custom Scrollbar -->
    <div class="flex-1 overflow-y-auto custom-scrollbar" :class="isDarkMode ? 'bg-gray-900' : 'bg-white'">
      <div v-if="loading" class="flex flex-col items-center justify-center h-48">
        <div class="spinner-small" :class="isDarkMode ? 'border-gray-600 border-t-blue-500' : ''"></div>
        <p class="text-xs mt-2" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">Loading vessels...</p>
      </div>
      
      <div v-else-if="filteredList.length === 0" class="flex flex-col items-center justify-center h-48" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
        <i class="fas fa-ship text-3xl mb-2 opacity-30"></i>
        <p class="text-xs">No vessels found</p>
      </div>
      
      <div v-else class="divide-y" :class="isDarkMode ? 'divide-gray-800' : 'divide-gray-100'">
        <div
          v-for="ship in filteredList"
          :key="ship.mmsi"
          @click="selectShip(ship)"
          class="p-2 cursor-pointer transition-colors relative"
          :class="[
            isDarkMode 
              ? (isSelected(ship.mmsi) ? 'bg-gray-800' : 'hover:bg-gray-800/50')
              : (isSelected(ship.mmsi) ? 'bg-blue-50' : 'hover:bg-gray-50')
          ]"
        >
          <!-- Distance Badge -->
          <div 
            v-if="ship.distance !== undefined && ship.distance !== null && ship.distance !== Infinity"
            class="absolute top-2 right-2 text-[10px] font-mono px-1 py-0.5 rounded-full"
            :class="getDistanceClass(ship.distance)"
          >
            {{ formatDistance(ship.distance) }}
          </div>
          
          <!-- Status Dot -->
          <div 
            class="absolute top-2 right-14 w-1.5 h-1.5 rounded-full"
            :class="isShipActive(ship) ? 'bg-green-500 animate-pulse' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')"
            :title="isShipActive(ship) ? 'Active (updated recently)' : 'Inactive (no recent update)'"
          ></div>
          
          <!-- Inactive Badge -->
          <div 
            v-if="!isShipActive(ship)"
            class="absolute top-6 right-2 text-[8px] px-1 py-0.5 rounded"
            :class="isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-500'"
          >
            inactive
          </div>
          
          <div class="flex items-start space-x-2 pr-16">
            <!-- Ship Icon -->
            <div 
              class="w-8 h-8 rounded-md flex items-center justify-center text-white flex-shrink-0"
              :style="{ backgroundColor: getShipColor(ship), opacity: isShipActive(ship) ? 1 : 0.6 }"
            >
              <i :class="getShipIcon(ship)" class="text-xs"></i>
            </div>
            
            <!-- Ship Info -->
            <div class="flex-1 min-w-0">
              <h3 class="font-medium truncate text-xs" :class="[
                isDarkMode 
                  ? (isShipActive(ship) ? 'text-gray-200' : 'text-gray-500') 
                  : (isShipActive(ship) ? 'text-gray-800' : 'text-gray-400'),
                !isShipActive(ship) && 'line-through'
              ]">
                {{ ship.name || 'Unknown' }}
                <span v-if="ship.distance !== undefined && ship.distance !== null && ship.distance !== Infinity" class="text-[10px] ml-1" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
                  (~{{ formatDistance(ship.distance) }})
                </span>
                <span v-else class="text-[10px] ml-1" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'">
                  (no position)
                </span>
              </h3>
              
              <div class="text-[10px] mt-0.5" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">
                <span class="font-mono">{{ ship.mmsi }}</span>
                <span v-if="ship.source" class="ml-1">• {{ ship.source }}</span>
                <span v-if="ship.callSign" class="ml-1">• {{ ship.callSign }}</span>
                <span v-if="ship.dest" class="ml-1">• {{ ship.dest.substring(0, 12) }}...</span>
              </div>
              
              <div class="flex items-center justify-between mt-1 text-[10px]">
                <div class="flex items-center space-x-1.5">
                  <span class="transition-colors" :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'" :title="'Speed: ' + (ship.sog || 0) + ' kts'">
                    <i class="fas fa-tachometer-alt mr-0.5 text-[9px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'"></i>
                    {{ formatSpeed(ship.sog) }}
                  </span>
                  
                  <span v-if="ship.cog !== undefined && ship.cog !== null" class="transition-colors" :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'" :title="'Course: ' + ship.cog.toFixed(0) + '°'">
                    <i class="fas fa-location-arrow mr-0.5 text-[9px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'" 
                       :style="{ transform: `rotate(${ship.cog}deg)` }"></i>
                    {{ ship.cog.toFixed(0) }}°
                  </span>
                  
                  <span v-if="ship.heading !== undefined && ship.heading !== null" class="transition-colors" :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'" :title="'Heading: ' + ship.heading + '°'">
                    <i class="fas fa-compass mr-0.5 text-[9px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'"></i>
                    {{ ship.heading }}°
                  </span>
                </div>
                
                <div class="text-[9px]" :class="isDarkMode ? 'text-gray-500' : 'text-gray-400'" :title="formatDateTime(ship.ts)">
                  {{ formatTimeAgo(ship.ts) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Footer Stats -->
    <div class="border-t p-2 flex-shrink-0" :class="isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100 bg-gray-50'">
      <div class="flex justify-between text-[10px]" :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'">
        <span><i class="fas fa-circle text-[8px] text-green-500 mr-1"></i> Active: {{ activeCount }}</span>
        <span><i class="fas fa-circle text-[8px] text-gray-400 mr-1"></i> Inactive: {{ inactiveCount }}</span>
        <span><i class="fas fa-location-dot text-[8px] mr-1"></i> Nearest: {{ nearestDistance }}</span>
        <span><i class="fas fa-chart-line text-[8px] mr-1"></i> Total: {{ filteredList.length }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { formatDistanceToNow, format } from 'date-fns'
import { useSelectedShipStore } from '../../stores/selectedShip'
import { useAuthStore } from '../../stores/auth'
import { useUIStore } from '../../stores/ui'
import { useFilterStore } from '../../stores/filter'
import { useCamerasStore } from '../../stores/cameras'
import { useThemeStore } from '../../stores/theme'

const router = useRouter()
const route = useRoute()
const selectedShipStore = useSelectedShipStore()
const authStore = useAuthStore()
const uiStore = useUIStore()
const filterStore = useFilterStore()
const camerasStore = useCamerasStore()
const themeStore = useThemeStore()

const props = defineProps({
  ships: { type: Array, required: true },
  selectedShip: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  sources: { type: Array, default: () => [] }
})

const emit = defineEmits([
  'select-ship', 
  'refresh', 
  'filtered-count-change', 
  'close', 
  'source-filter-change'
])

// Dark mode
const isDarkMode = computed(() => themeStore.isDarkMode)

// Filter section visibility - saved to localStorage
const showFilterSection = ref(localStorage.getItem('sidebar_show_filters') !== 'false')

// Search query
const searchQuery = ref('')
const sourceFilter = ref(uiStore.adminSourceFilter || 'all')
const statusFilter = ref(filterStore.filterStatus || 'all') // 'all', 'active', 'inactive'
const referenceLocation = ref(null)

// Get all unique sources from both AIS and Cameras (only for admin)
const allSources = computed(() => {
  const sourcesSet = new Set()
  
  // Add sources from AIS ships
  props.ships.forEach(ship => {
    if (ship.source) sourcesSet.add(ship.source)
  })
  
  // Add sources from cameras
  camerasStore.cameras.forEach(cam => {
    if (cam.source) sourcesSet.add(cam.source)
  })
  
  // Also include from props.sources (from auth)
  props.sources.forEach(source => {
    sourcesSet.add(source)
  })
  
  return Array.from(sourcesSet).sort()
})

// Gunakan filterStore (type filter dan status filter)
const typeFilter = computed({
  get: () => filterStore.filterType,
  set: (val) => {
    filterStore.setFilterType(val)
  }
})

const isAdmin = computed(() => authStore.user?.role === 'admin' || authStore.userSource === 'all')
const selectedShipMmsi = computed(() => selectedShipStore.selectedShipMmsi)

// Check if any active filters
const hasActiveFilters = computed(() => {
  return typeFilter.value !== 'all' || 
         searchQuery.value !== '' || 
         (isAdmin.value && sourceFilter.value !== 'all') ||
         statusFilter.value !== 'all'
})

// ============================================
// PERBAIKAN UTAMA: Fungsi getShipTypeFromCode yang lengkap sesuai standar AIS IMO
// ============================================
function getShipTypeFromCode(code) {
  // Handle invalid input
  if (code === undefined || code === null || code === '') return 'unknown'
  
  // Convert to number if string
  const numCode = typeof code === 'string' ? parseInt(code, 10) : code
  
  // Validate number
  if (isNaN(numCode) || numCode < 0 || numCode > 99) return 'unknown'
  
  // Special handling for specific codes first
  const specificTypes = {
    // Kode 3x (Fishing, Towing, Special Ops)
    30: 'fishing',
    31: 'tug',
    32: 'towing',        // Towing (long/wide)
    33: 'dredging',      // Dredging or underwater ops
    34: 'diving',        // Diving ops
    35: 'military',      // Military ops
    36: 'sailing',
    37: 'pleasure',
    
    // Kode 5x (Special Vessels)
    50: 'pilot',         // Pilot vessel
    51: 'sar',           // Search and Rescue
    52: 'tug',
    53: 'port_tender',   // Port tender
    54: 'anti_pollution', // Anti-pollution equipment
    55: 'law',           // Law enforcement
    56: 'local',         // Spare - Local vessel
    57: 'local',         // Spare - Local vessel
    58: 'medical',       // Medical transport
    59: 'non_combatant', // Non-combatant ship (RO-RO)
    
    // Kode 9x (Other Types)
    90: 'other',
    91: 'other',
    92: 'other',
    93: 'other',
    94: 'other',
    95: 'other',
    96: 'other',
    97: 'other',
    98: 'other',
    99: 'aton'           // Aids to Navigation
  }
  
  // Check for specific code mapping
  if (specificTypes[numCode]) {
    return specificTypes[numCode]
  }
  
  // Get first digit for category
  const firstDigit = Math.floor(numCode / 10)
  
  // Category-based mapping
  switch (firstDigit) {
    case 0:
      return 'not_available'
    case 1:
      return 'reserved'
    case 2:
      // Wing In Ground effect (WIG) - NOT High Speed Craft
      if (numCode >= 20 && numCode <= 29) {
        return 'wig'
      }
      return 'unknown'
    case 3:
      // Fishing, Towing, Special Ops, Sailing, Pleasure
      if (numCode >= 30 && numCode <= 39) {
        return 'special_vessel'
      }
      return 'unknown'
    case 4:
      // High Speed Craft (HSC)
      if (numCode >= 40 && numCode <= 49) {
        return 'hsc'
      }
      return 'unknown'
    case 5:
      // Special vessels (Pilot, SAR, Tug, etc.)
      if (numCode >= 50 && numCode <= 59) {
        return 'special_vessel'
      }
      return 'unknown'
    case 6:
      // Passenger vessels
      if (numCode >= 60 && numCode <= 69) {
        return 'passenger'
      }
      return 'unknown'
    case 7:
      // Cargo vessels
      if (numCode >= 70 && numCode <= 79) {
        return 'cargo'
      }
      return 'unknown'
    case 8:
      // Tanker vessels
      if (numCode >= 80 && numCode <= 89) {
        return 'tanker'
      }
      return 'unknown'
    case 9:
      // Other types
      if (numCode >= 90 && numCode <= 99) {
        return 'other'
      }
      return 'unknown'
    default:
      return 'unknown'
  }
}

// Helper function to get human-readable vessel type display name
function getVesselTypeDisplayName(typeKey) {
  const displayNames = {
    cargo: 'Cargo',
    tanker: 'Tanker',
    passenger: 'Passenger',
    fishing: 'Fishing',
    tug: 'Tug',
    towing: 'Towing',
    sailing: 'Sailing',
    pleasure: 'Pleasure',
    hsc: 'High Speed Craft',
    wig: 'Wing In Ground',
    military: 'Military',
    law: 'Law Enforcement',
    sar: 'SAR',
    medical: 'Medical',
    pilot: 'Pilot Vessel',
    port_tender: 'Port Tender',
    anti_pollution: 'Anti-Pollution',
    dredging: 'Dredging/Underwater',
    diving: 'Diving Ops',
    aton: 'Aids to Navigation',
    local: 'Local Vessel',
    not_available: 'Not Available',
    reserved: 'Reserved',
    special_vessel: 'Special Vessel',
    other: 'Other',
    unknown: 'Unknown'
  }
  return displayNames[typeKey] || 'Vessel'
}

// Get display name for ship type (for filter UI)
function getTypeDisplayName(type) {
  return getVesselTypeDisplayName(type)
}

// Handle status filter change
function handleStatusFilterChange() {
  filterStore.setFilterStatus(statusFilter.value)
}

// Handle type filter change
function handleTypeFilterChange() {
  filterStore.setFilterType(typeFilter.value)
}

// Check if ship is active (updated within last 2 hours)
function isShipActive(ship) {
  if (!ship.ts) return false
  const shipTime = new Date(ship.ts).getTime()
  const now = Date.now()
  const twoHoursInMs = 2 * 60 * 60 * 1000
  return shipTime > now - twoHoursInMs
}

// Toggle filter section visibility
function toggleFilterSection() {
  showFilterSection.value = !showFilterSection.value
  localStorage.setItem('sidebar_show_filters', showFilterSection.value)
}

// Reset functions
function resetTypeFilter() {
  filterStore.setFilterType('all')
}

function resetSourceFilter() {
  sourceFilter.value = 'all'
  uiStore.setAdminSourceFilter('all')
  emit('source-filter-change', 'all')
}

function resetStatusFilter() {
  statusFilter.value = 'all'
  filterStore.setFilterStatus('all')
}

function clearAllFilters() {
  resetTypeFilter()
  searchQuery.value = ''
  resetStatusFilter()
  if (isAdmin.value && sourceFilter.value !== 'all') {
    resetSourceFilter()
  }
}

// Watch for active page changes to clear selection when leaving map
watch(() => uiStore.activePage, (newPage) => {
  if (newPage !== 'map') {
    selectedShipStore.clearSelection()
  }
}, { immediate: true })

// Sync source filter with store
watch(() => uiStore.adminSourceFilter, (newFilter) => {
  if (sourceFilter.value !== newFilter) {
    sourceFilter.value = newFilter
  }
})

// Sync status filter with store
watch(() => filterStore.filterStatus, (newStatus) => {
  if (statusFilter.value !== newStatus) {
    statusFilter.value = newStatus
  }
})

// Handle source filter change - only for admin
function handleSourceFilterChange() {
  uiStore.setAdminSourceFilter(sourceFilter.value)
  emit('source-filter-change', sourceFilter.value)
}

function isSelected(mmsi) {
  return selectedShipMmsi.value === mmsi
}

function hasValidCoordinates(ship) {
  if (!ship.lat || !ship.lng) return false
  if (isNaN(ship.lat) || isNaN(ship.lng)) return false
  if (Math.abs(ship.lat) > 90 || Math.abs(ship.lng) > 180) return false
  return true
}

function calculateDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity
  
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

function getReferenceLocation() {
  if (authStore.userLocation && authStore.userLocation.lat && authStore.userLocation.lng) {
    return {
      lat: authStore.userLocation.lat,
      lng: authStore.userLocation.lng,
      name: authStore.userSourceName || 'Tower Location'
    }
  }
  
  const userData = localStorage.getItem('user')
  if (userData) {
    try {
      const user = JSON.parse(userData)
      if (user.location && user.location.lat && user.location.lng) {
        return {
          lat: user.location.lat,
          lng: user.location.lng,
          name: user.sourceName || user.username || 'Tower Location'
        }
      }
    } catch (e) {
      console.error('Error parsing user location:', e)
    }
  }
  
  return null
}

const mapFilteredShips = computed(() => {
  let result = [...props.ships]
  
  result = result.filter(ship => hasValidCoordinates(ship))
  
  // Filter AIS by source (ONLY for admin)
  if (isAdmin.value && sourceFilter.value !== 'all') {
    result = result.filter(ship => ship.source === sourceFilter.value)
  } else if (!isAdmin.value && authStore.userSource !== 'all') {
    result = result.filter(ship => ship.source === authStore.userSource)
  }
  
  // Filter by ship type
  if (filterStore.filterType !== 'all') {
    result = result.filter(ship => {
      const shipType = getShipTypeFromCode(ship.type_code)
      return shipType === filterStore.filterType
    })
  }
  
  // Filter by AIS status (active/inactive)
  if (statusFilter.value !== 'all') {
    const isActive = statusFilter.value === 'active'
    result = result.filter(ship => isShipActive(ship) === isActive)
  }
  
  return result
})

const shipsWithDistance = computed(() => {
  const refLoc = referenceLocation.value
  if (!refLoc) return mapFilteredShips.value
  
  return mapFilteredShips.value.map(ship => {
    if (hasValidCoordinates(ship)) {
      const distance = calculateDistance(refLoc.lat, refLoc.lng, ship.lat, ship.lng)
      return { ...ship, distance }
    }
    return { ...ship, distance: Infinity }
  })
})

const filteredList = computed(() => {
  let result = [...shipsWithDistance.value]
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(ship =>
      (ship.name?.toLowerCase().includes(query)) ||
      (ship.mmsi?.includes(query)) ||
      (ship.callSign?.toLowerCase().includes(query))
    )
  }
  
  // Remove duplicates by MMSI
  const uniqueByMmsi = new Map()
  for (const ship of result) {
    const mmsiKey = String(ship.mmsi)
    if (!uniqueByMmsi.has(mmsiKey) || new Date(ship.ts) > new Date(uniqueByMmsi.get(mmsiKey).ts)) {
      uniqueByMmsi.set(mmsiKey, ship)
    }
  }
  result = Array.from(uniqueByMmsi.values())
  
  result.sort((a, b) => {
    const distA = a.distance !== undefined ? a.distance : Infinity
    const distB = b.distance !== undefined ? b.distance : Infinity
    return distA - distB
  })
  
  return result
})

watch(filteredList, (newList) => {
  emit('filtered-count-change', newList.length)
}, { immediate: true, deep: true })

const activeCount = computed(() => {
  return filteredList.value.filter(ship => isShipActive(ship)).length
})

const inactiveCount = computed(() => {
  return filteredList.value.filter(ship => !isShipActive(ship)).length
})

const nearestDistance = computed(() => {
  if (filteredList.value.length === 0) return 'N/A'
  const nearest = filteredList.value[0]
  if (nearest.distance && nearest.distance !== Infinity) {
    return formatDistance(nearest.distance)
  }
  return 'N/A'
})

function formatDistance(km) {
  if (km === undefined || km === null || km === Infinity) return 'N/A'
  if (km < 1) {
    return `${(km * 1000).toFixed(0)} m`
  }
  if (km < 10) {
    return `${km.toFixed(1)} km`
  }
  return `${km.toFixed(0)} km`
}

function getDistanceClass(km) {
  if (km === undefined || km === Infinity) return 'bg-gray-100 text-gray-500'
  if (km < 1) return 'bg-green-100 text-green-700'
  if (km < 5) return 'bg-blue-100 text-blue-700'
  if (km < 20) return 'bg-yellow-100 text-yellow-700'
  return 'bg-gray-100 text-gray-500'
}

function getShipColor(ship) {
  const type = getShipTypeFromCode(ship.type_code)
  const colors = {
    cargo: '#f59e0b',
    tanker: '#ef4444',
    passenger: '#14b8a6',
    tug: '#f97316',
    towing: '#f97316',
    fishing: '#22c55e',
    sailing: '#a855f7',
    pleasure: '#ec4899',
    hsc: '#db2777',
    wig: '#8b5cf6',
    military: '#6b7280',
    law: '#3b82f6',
    sar: '#dc2626',
    medical: '#e11d48',
    pilot: '#06b6d4',
    port_tender: '#6366f1',
    anti_pollution: '#10b981',
    dredging: '#f43f5e',
    diving: '#f97316',
    aton: '#eab308',
    local: '#94a3b8',
    special_vessel: '#a855f7',
    other: '#9ca3af',
    not_available: '#9ca3af',
    reserved: '#9ca3af',
    unknown: '#9ca3af'
  }
  return colors[type] || colors.unknown
}

function getShipIcon(ship) {
  // Semua kapal menggunakan icon kapal yang sama
  return 'fas fa-ship'
}

function formatSpeed(speed) {
  if (speed === undefined || speed === null) return '0 kts'
  return `${speed.toFixed(1)} kts`
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Never'
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
  } catch {
    return 'Invalid date'
  }
}

function formatDateTime(timestamp) {
  if (!timestamp) return 'Never'
  try {
    return format(new Date(timestamp), 'dd MMM yyyy HH:mm:ss')
  } catch {
    return 'Invalid date'
  }
}

function selectShip(ship) {
  selectedShipStore.selectShip(ship, 'sidebar')
  emit('select-ship', ship)
  
  if (route.path === '/') {
    console.log('Ship selected from sidebar on map view:', ship.name)
  } else if (route.path === `/ship/${ship.mmsi}`) {
    console.log('Ship selected from sidebar on detail view:', ship.name)
  } else if (route.path === '/playback') {
    console.log('Ship selected from sidebar on playback view:', ship.name)
  } else {
    router.push('/')
  }
}

onMounted(() => {
  referenceLocation.value = getReferenceLocation()
})

watch(() => props.ships, () => {
  if (!referenceLocation.value) {
    referenceLocation.value = getReferenceLocation()
  }
}, { immediate: true })
</script>

<style scoped>
.ship-sidebar { 
  width: 100%; 
  height: 100%; 
}

/* Custom scrollbar untuk semua theme */
.custom-scrollbar {
  scrollbar-width: thin;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Dark mode scrollbar */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #1f1f35;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.spinner-small {
  width: 24px; 
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.dark .spinner-small {
  border-color: #374151;
  border-top-color: #60a5fa;
}

@keyframes spin { 
  to { transform: rotate(360deg); } 
}

.animate-pulse { 
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; 
}

@keyframes pulse { 
  0%, 100% { opacity: 1; } 
  50% { opacity: 0.5; } 
}

.line-through {
  text-decoration: line-through;
}
</style>